import { useQuery } from "@tanstack/react-query";
import { ChevronRightIcon } from "lucide-react";
import { useState } from "react";
import { Link, Button as RACButton } from "react-aria-components";

import apiClient from "@/lib/api/client";
import { cn } from "@/utils/cn";

import { Tooltip, TooltipTrigger } from "../../tooltip";
import User from "../icons/user.svg?react";
import Users from "../icons/users.svg?react";

type TeamMember = {
  email: string;
  id: string;
  name: string;
};

type TeamMembersResponse = {
  teamMembers: TeamMember[];
};

type NavItemProps = {
  isActive: boolean;
  isCollapsed: boolean;
  userId: number;
};

const getNavItemBaseClass = (isCollapsed: boolean) =>
  cn(
    "flex p-md text-sm font-semibold text-text-secondary hover:text-text-primary",
    isCollapsed ? "mx-auto w-fit justify-center" : "gap-3 px-lg",
  );

const navItemStyles = {
  button: "w-full outline-hidden",
  icon: "size-5 text-nav-item-icon-fg",
};

function useTeamMembers(userId: number) {
  return useQuery<TeamMembersResponse>({
    queryFn: () => apiClient.url(`/users/${userId}/team-members`).get().json(),
    queryKey: ["user", userId, "team-members"],
  });
}

type TeamMemberItemProps = {
  isCollapsed: boolean;
  member: TeamMember;
};

function TeamMemberItem({ isCollapsed, member }: TeamMemberItemProps) {
  return (
    <li>
      <TooltipTrigger delay={250}>
        <Link
          className={cn(getNavItemBaseClass(isCollapsed), navItemStyles.button)}
          href={`/users/${member.id}/edit`}
        >
          <Users className={cn(navItemStyles.icon, "shrink-0")} />
          {!isCollapsed && <span className="truncate">{member.name}</span>}
        </Link>
        {isCollapsed && <Tooltip placement="right">{member.name}</Tooltip>}
      </TooltipTrigger>
    </li>
  );
}

function ViewMoreItem({ isCollapsed }: { isCollapsed: boolean }) {
  if (isCollapsed) return null;

  return (
    <li>
      <TooltipTrigger delay={250}>
        <Link
          className={cn(getNavItemBaseClass(false), navItemStyles.button)}
          href="/users"
        >
          <span className="truncate">View more</span>
        </Link>
      </TooltipTrigger>
    </li>
  );
}

export function TeamMembersNavItem({
  isActive,
  isCollapsed,
  userId,
}: NavItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data, isError, isLoading } = useTeamMembers(userId);

  const toggleExpanded = () => setIsExpanded((prev) => !prev);

  if (isError) {
    return null;
  }

  return (
    <>
      <li>
        <TooltipTrigger delay={250}>
          <RACButton
            aria-expanded={isExpanded}
            className={navItemStyles.button}
            onPress={toggleExpanded}
          >
            <div
              className={cn(
                getNavItemBaseClass(isCollapsed),
                isActive &&
                  "rounded-md bg-bg-primary text-fg-brand-primary shadow-xs",
              )}
            >
              <User
                className={cn(
                  navItemStyles.icon,
                  "shrink-0",
                  isActive && "text-fg-brand-primary",
                )}
              />
              {!isCollapsed && (
                <>
                  <span className="whitespace-nowrap">Users</span>
                  <ChevronRightIcon
                    className={cn(
                      "ml-auto size-5 shrink-0 text-gray-500 transition-transform duration-200",
                      isExpanded && "rotate-90",
                    )}
                  />
                </>
              )}
            </div>
          </RACButton>
          {isCollapsed && <Tooltip placement="right">Users</Tooltip>}
        </TooltipTrigger>
      </li>

      {isExpanded && !isLoading && (
        <ul className="space-y-1">
          {data?.teamMembers.map((member) => (
            <TeamMemberItem
              isCollapsed={isCollapsed}
              key={member.id}
              member={member}
            />
          ))}
          <ViewMoreItem isCollapsed={isCollapsed} />
        </ul>
      )}
    </>
  );
}
